import { call, put, select } from 'redux-saga/effects';

import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { selectDataPatient } from 'connect/selectors';
import { POST, request } from 'utils/request';

import { SagaHandler } from '../SagaHandler';

const apiRequestPhoneVerification = ({ patientId, phone }) => request(
  POST,
  `${CONFIG.API_URL}/patient/${patientId}/requestPatientPhoneVerification`,
  {
    phone,
  },
);

const apiVerifyPhone = ({ patientId, phone, code }) => request(
  POST,
  `${CONFIG.API_URL}/patient/${patientId}/verifyPatientPhone`,
  {
    phone,
    code,
  },
);

/**
 * Requests a new phone verification code for a patient
 */
class _RequestPatientPhoneVerification extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/REQUEST_PATIENT_PHONE_VERIFICATION',
      stateKey: 'requestPatientPhoneVerification',
    });
  }

  createAction({ phone }) {
    return {
      type: this._actionName,
      phone,
    };
  }

  * saga({ phone }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const valueHandler = this.value;
    const successHandler = this.success;

    try {
      yield put(loadingHandler.update(true));
      yield put(errorHandler.update(null));
      yield put(valueHandler.update(null));
      yield put(successHandler.update(false));

      const patient = yield select(selectDataPatient);
      if (!patient?.id) {
        throw new Error('No patient in Redux state, cannot request phone verification');
      }

      const requestData = yield call(apiRequestPhoneVerification, { patientId: patient.id, phone });
      yield put(valueHandler.update(requestData));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      yield put(errorHandler.update(errorMessage));
      yield put(valueHandler.update(null));
      yield put(successHandler.update(false));

      sendToLogger(error, 'RequestPatientPhoneVerification');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

/**
 * Verifies a phone number for a patient
 */
class _VerifyPatientPhone extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/VERIFY_PATIENT_PHONE',
      stateKey: 'verifyPatientPhone',
    });
  }

  createAction({ phone, code }) {
    return {
      type: this._actionName,
      phone,
      code,
    };
  }

  * saga({ phone, code }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const valueHandler = this.value;
    const successHandler = this.success;

    try {
      yield put(loadingHandler.update(true));
      yield put(errorHandler.update(null));
      yield put(valueHandler.update(null));
      yield put(successHandler.update(false));

      const patient = yield select(selectDataPatient);
      if (!patient?.id) {
        throw new Error('No patient in Redux state, cannot verify phone');
      }

      const requestData = yield call(apiVerifyPhone, { patientId: patient.id, phone, code });
      yield put(valueHandler.update(requestData));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      yield put(errorHandler.update(errorMessage));
      yield put(valueHandler.update(null));
      yield put(successHandler.update(false));

      sendToLogger(error, 'VerifyPatientPhone');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const RequestPatientPhoneVerification = new _RequestPatientPhoneVerification();
export const VerifyPatientPhone = new _VerifyPatientPhone();
